import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import { TrustBanner } from '../../components/TrustSignals';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Clock, MapPin, Truck, Battery, Car, Key, Wrench, Shield,
  ChevronRight, MessageCircle, Loader2, ShoppingBag, Tag
} from 'lucide-react';
import { ExportPDFButton } from '../../components/ExportPDFButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceIcons = {
  tow: Truck,
  jump_start: Battery,
  flat_tire: Car,
  lockout: Key,
  repair: Wrench,
  diagnostic: Shield
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  accepted: 'bg-blue-100 text-blue-800 border-blue-200',
  en_route: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  arrived: 'bg-purple-100 text-purple-800 border-purple-200',
  in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

export default function CustomerDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/services`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeRequest = requests.find(r => 
    !['completed', 'cancelled'].includes(r.status)
  );

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    setChatLoading(true);
    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: chatMessage },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setChatResponse(response.data.response);
    } catch (error) {
      toast.error('Chat service unavailable');
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="customer-dashboard">
      <Header />
      
      {/* Trust Banner */}
      <TrustBanner variant="compact" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 pb-20 sm:pb-8">
        {/* Welcome */}
        <div className="mb-4 sm:mb-8 flex items-start justify-between animate-fade-in-up">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#191919]">
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">How can we help you today?</p>
          </div>
          <ExportPDFButton reportType="customer" label="Export Report" />
        </div>

        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="flex-1 lg:flex-[2] space-y-4 sm:space-y-6 order-2 lg:order-1">
            {/* Active Request Card */}
            {activeRequest ? (
              <Card className="border-2 border-[#FF3008]/20" data-testid="active-request-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-gray-900">Active Request</CardTitle>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[activeRequest.status]}`}>
                      {activeRequest.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#FFF0ED] rounded-lg flex items-center justify-center">
                      {serviceIcons[activeRequest.service_type] && 
                        React.createElement(serviceIcons[activeRequest.service_type], { className: 'w-6 h-6 text-[#FF3008]' })}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 capitalize">
                        {activeRequest.service_type.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-gray-600">
                        {activeRequest.vehicle_year} {activeRequest.vehicle_make} {activeRequest.vehicle_model}
                      </p>
                    </div>
                  </div>
                  
                  {activeRequest.mechanic_name && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
                      <p className="text-sm text-gray-500 mb-1">Your Mechanic</p>
                      <p className="font-semibold text-gray-900">{activeRequest.mechanic_name}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    {['accepted', 'en_route'].includes(activeRequest.status) && (
                      <Link to={`/customer/tracking/${activeRequest.request_id}`} className="flex-1">
                        <Button className="w-full bg-[#FF3008] hover:bg-[#D42600]" data-testid="track-mechanic-btn">
                          <MapPin className="w-4 h-4 mr-2" />
                          Track Mechanic
                        </Button>
                      </Link>
                    )}
                    <Link to={`/customer/tracking/${activeRequest.request_id}`} className="flex-1">
                      <Button variant="outline" className="w-full" data-testid="view-details-btn">
                        View Details
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-dashed border-gray-300" data-testid="no-active-request">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Car className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Requests</h3>
                  <p className="text-gray-600 mb-6">Need roadside assistance or mobile mechanic service?</p>
                  <Link to="/customer/request" data-testid="request-service-btn">
                    <Button className="bg-[#FF3008] hover:bg-[#D42600]">
                      <Plus className="w-4 h-4 mr-2" />
                      Request Service
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="glass animate-fade-in-up stagger-2" data-testid="quick-actions">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
                  {[
                    { icon: Truck, label: 'Towing', type: 'tow' },
                    { icon: Battery, label: 'Jump Start', type: 'jump_start' },
                    { icon: Car, label: 'Flat Tire', type: 'flat_tire' },
                    { icon: Key, label: 'Lockout', type: 'lockout' },
                    { icon: Wrench, label: 'Repair', type: 'repair' },
                    { icon: Shield, label: 'Diagnostic', type: 'diagnostic' },
                  ].map((service) => (
                    <Link 
                      key={service.type}
                      to={`/customer/request?type=${service.type}`}
                      className="p-2 sm:p-4 border border-[#E8E8E8] rounded-xl hover:border-[#FF3008]/30 hover:bg-[#FFF0ED] transition-all group text-center hover-lift btn-press"
                      data-testid={`quick-action-${service.type}`}
                    >
                      <service.icon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 group-hover:text-[#FF3008] mx-auto mb-1 sm:mb-2 transition-colors" />
                      <p className="text-xs sm:text-sm font-medium text-gray-700 group-hover:text-[#FF3008] truncate">{service.label}</p>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Requests */}
            <Card data-testid="recent-requests">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Recent Requests</CardTitle>
                  <Link to="/customer/history" className="text-[#FF3008] text-sm font-medium hover:underline">
                    View All
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-[#FF3008] animate-spin" />
                  </div>
                ) : requests.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No service requests yet</p>
                ) : (
                  <div className="space-y-3">
                    {requests.slice(0, 3).map((request) => {
                      const ServiceIcon = serviceIcons[request.service_type] || Car;
                      return (
                        <Link 
                          key={request.request_id}
                          to={`/customer/tracking/${request.request_id}`}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          data-testid={`request-item-${request.request_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <ServiceIcon className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 capitalize">
                                {request.service_type.replace('_', ' ')}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(request.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[request.status]}`}>
                              {request.status.replace('_', ' ')}
                            </span>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Shows first on mobile */}
          <div className="flex-1 lg:flex-[1] space-y-4 sm:space-y-6 order-1 lg:order-2">
            {/* Request Service CTA */}
            <Card className="bg-[#FF3008] text-white shadow-xl animate-fade-in-up" data-testid="request-cta">
              <CardContent className="py-6">
                <h3 className="text-xl font-bold mb-2">Need Help?</h3>
                <p className="text-white/80 mb-4">Get roadside assistance in minutes</p>
                <Link to="/customer/request">
                  <Button className="w-full bg-white text-[#FF3008] hover:bg-gray-100 font-bold rounded-full" data-testid="sidebar-request-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Request Service
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* AI Chat Assistant */}
            <Card className="glass animate-fade-in-up stagger-2" data-testid="ai-chat">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#FF3008]" />
                  AI Assistant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Describe your car problem and get instant help
                </p>
                <form onSubmit={handleChat}>
                  <textarea
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="e.g., My car won't start and makes a clicking sound..."
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#FF3008] focus:border-transparent"
                    rows={3}
                    data-testid="ai-chat-input"
                  />
                  <Button 
                    type="submit" 
                    className="w-full mt-3 bg-[#FF3008] hover:bg-[#D42600] rounded-full"
                    disabled={chatLoading}
                    data-testid="ai-chat-submit"
                  >
                    {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Get Help'}
                  </Button>
                </form>
                {chatResponse && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700" data-testid="ai-chat-response">
                    {chatResponse}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card className="glass animate-fade-in-up stagger-3" data-testid="emergency-contact">
              <CardContent className="py-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">24/7 Hotline</p>
                    <p className="text-sm text-gray-600">For emergencies</p>
                  </div>
                </div>
                <a href="tel:2602183536" className="text-2xl font-bold text-[#FF3008] hover:underline">(260) 218-3536</a>
              </CardContent>
            </Card>

            {/* Marketplace Promotion */}
            <Card className="bg-[#FFF0ED] border-[#FF3008]/20 animate-fade-in-up stagger-4" data-testid="marketplace-promo">
              <CardContent className="py-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#FF3008] rounded-xl flex items-center justify-center">
                    <ShoppingBag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Jiffys Marketplace</p>
                    <p className="text-sm text-gray-600">Buy & sell auto parts, tools & more</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Link to="/marketplace">
                    <Button className="w-full bg-[#FF3008] hover:bg-[#D42600] rounded-full" data-testid="browse-marketplace-btn">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Browse Marketplace
                    </Button>
                  </Link>
                  <Link to="/marketplace/sell">
                    <Button variant="outline" className="w-full border-[#FF3008]/30 text-[#FF3008] hover:bg-[#FFF0ED] rounded-full" data-testid="sell-something-btn">
                      <Tag className="w-4 h-4 mr-2" />
                      Sell Something
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
